import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config.js';
import { videoApi } from '../api/video.js';
import EnhancedVideoPlayer from './EnhancedVideoPlayerV2.js';
import './AthleteFeedbackView.css';

function AthleteFeedbackView({ athleteId, athleteName }) {
  const [videosWithNotes, setVideosWithNotes] = useState([]);
  const [feedback, setFeedback] = useState([]);
  const [progressStats, setProgressStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('week'); // 'week' or 'month'
  const [selectedEvent, setSelectedEvent] = useState('All'); // 'All', 'Vault', 'Bars', 'Beam', 'Floor'

  const token = JSON.parse(localStorage.getItem('user'))?.token;

  const resolveSrc = (url) => (!url ? '' : (url.startsWith('http') ? url : `${API_BASE_URL}${url}`));

  const acknowledge = async (id) => {
    try {
      await videoApi.acknowledgeFeedback(id);
      setFeedback(fb => fb.map(f => f.id === id ? { ...f, acknowledged: 1 } : f));
    } catch { /* ignore */ }
  };

  useEffect(() => {
    if (!athleteId || !token) return;

    const fetchFeedbackData = async () => {
      setLoading(true);
      try {
        // Fetch recent videos with coach notes
        const videosResponse = await axios.get(
          `${API_BASE_URL}/athletes/${athleteId}/videos`,
          {
            params: { limit: 50 }, // Get last 50 videos
            headers: { Authorization: `Bearer ${token}` }
          }
        );

        // Filter videos that have coach notes (once backend stores them)
        // For now, we'll show all recent videos
        const videos = videosResponse.data || [];
        
        setVideosWithNotes(videos);
        calculateProgressStats(videos);

        // Real coach feedback feed (notes a coach left on specific clips)
        try {
          setFeedback(await videoApi.getAthleteFeedback(athleteId));
        } catch {
          setFeedback([]);
        }
      } catch (err) {
        console.error('Failed to fetch feedback data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchFeedbackData();
  }, [athleteId, token, timeRange]);

  const calculateProgressStats = (videos) => {
    const now = new Date();
    const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    const twoWeeksAgo = new Date(now.getTime() - 14 * 24 * 60 * 60 * 1000);
    const monthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);

    const thisWeek = videos.filter(v => new Date(v.created_at) >= weekAgo);
    const lastWeek = videos.filter(v => {
      const date = new Date(v.created_at);
      return date >= twoWeeksAgo && date < weekAgo;
    });
    const thisMonth = videos.filter(v => new Date(v.created_at) >= monthAgo);

    // Calculate success rates by event
    const calculateEventStats = (videoList, event) => {
      const eventVideos = event === 'All' 
        ? videoList 
        : videoList.filter(v => v.event === event);
      
      // TODO: Once we have landing_quality stored, calculate actual success rate
      // For now, return video counts
      return {
        total: eventVideos.length,
        byEvent: {
          Vault: videoList.filter(v => v.event === 'Vault').length,
          Bars: videoList.filter(v => v.event === 'Bars').length,
          Beam: videoList.filter(v => v.event === 'Beam').length,
          Floor: videoList.filter(v => v.event === 'Floor').length
        }
      };
    };

    setProgressStats({
      thisWeek: calculateEventStats(thisWeek, selectedEvent),
      lastWeek: calculateEventStats(lastWeek, selectedEvent),
      thisMonth: calculateEventStats(thisMonth, selectedEvent)
    });
  };

  // Recalculate when event filter changes
  useEffect(() => {
    if (videosWithNotes.length > 0) {
      calculateProgressStats(videosWithNotes);
    }
  }, [selectedEvent]);

  if (loading) {
    return <div style={{ padding: '2rem', textAlign: 'center' }}>Loading feedback...</div>;
  }

  return (
    <div className="athlete-feedback-view">
      {/* Progress Stats Section */}
      <div className="progress-stats-section">
        <h2>📊 Your Progress</h2>
        
        <div className="event-filter">
          <label>Event:</label>
          <select value={selectedEvent} onChange={(e) => setSelectedEvent(e.target.value)}>
            <option value="All">All Events</option>
            <option value="Vault">Vault</option>
            <option value="Bars">Bars</option>
            <option value="Beam">Beam</option>
            <option value="Floor">Floor</option>
          </select>
        </div>

        {progressStats && (
          <div className="stats-cards">
            <div className="stat-card">
              <h3>This Week</h3>
              <div className="stat-number">{progressStats.thisWeek.total}</div>
              <div className="stat-label">Videos Recorded</div>
              {selectedEvent === 'All' && (
                <div className="stat-breakdown">
                  <span>Vault: {progressStats.thisWeek.byEvent.Vault}</span>
                  <span>Bars: {progressStats.thisWeek.byEvent.Bars}</span>
                  <span>Beam: {progressStats.thisWeek.byEvent.Beam}</span>
                  <span>Floor: {progressStats.thisWeek.byEvent.Floor}</span>
                </div>
              )}
            </div>

            <div className="stat-card">
              <h3>Last Week</h3>
              <div className="stat-number">{progressStats.lastWeek.total}</div>
              <div className="stat-label">Videos Recorded</div>
              {selectedEvent === 'All' && (
                <div className="stat-breakdown">
                  <span>Vault: {progressStats.lastWeek.byEvent.Vault}</span>
                  <span>Bars: {progressStats.lastWeek.byEvent.Bars}</span>
                  <span>Beam: {progressStats.lastWeek.byEvent.Beam}</span>
                  <span>Floor: {progressStats.lastWeek.byEvent.Floor}</span>
                </div>
              )}
            </div>

            <div className="stat-card trend-card">
              <h3>Trend</h3>
              <div className="trend-indicator">
                {progressStats.thisWeek.total > progressStats.lastWeek.total ? (
                  <span className="trend-up">📈 +{progressStats.thisWeek.total - progressStats.lastWeek.total}</span>
                ) : progressStats.thisWeek.total < progressStats.lastWeek.total ? (
                  <span className="trend-down">📉 {progressStats.thisWeek.total - progressStats.lastWeek.total}</span>
                ) : (
                  <span className="trend-neutral">➡️ No change</span>
                )}
              </div>
              <div className="stat-label">vs. Last Week</div>
            </div>
          </div>
        )}
      </div>

      {/* Coach Feedback Section */}
      <div className="feedback-section">
        <h2>💬 Coach Feedback & Corrections</h2>

        {feedback.length === 0 ? (
          <div className="no-feedback-message">
            <p>📹 No coach feedback yet.</p>
            <p style={{ fontSize: '0.9rem', color: '#666' }}>
              Your coach will add corrections here after reviewing your videos.
            </p>
          </div>
        ) : (
          <div className="feedback-videos-list">
            {feedback.map((f) => (
              <div key={f.id} className="feedback-video-card">
                <div className="feedback-card-header">
                  <div className="video-meta">
                    <span className="event-badge" data-event={f.event}>
                      {f.event || 'Clip'}
                    </span>
                    <span className="video-date">
                      {new Date(f.created_at).toLocaleDateString('en-US', {
                        weekday: 'short', month: 'short', day: 'numeric',
                        hour: '2-digit', minute: '2-digit'
                      })}
                    </span>
                    {f.acknowledged
                      ? <span style={{ marginLeft: 'auto', color: '#27ae60', fontSize: '0.85rem' }}>✓ Seen</span>
                      : <button
                          onClick={() => acknowledge(f.id)}
                          style={{ marginLeft: 'auto', fontSize: '0.8rem', padding: '0.25rem 0.6rem', cursor: 'pointer' }}
                        >Mark seen</button>}
                  </div>
                </div>

                <div className="feedback-content">
                  <div className="video-section">
                    <EnhancedVideoPlayer videoSrc={resolveSrc(f.url)} videoId={f.video_id} fps={f.fps || 30} poseJsonPath={f.pose_json_path || null} readOnly />
                  </div>

                  <div className="coach-notes-section">
                    <div className="notes-header">
                      <span className="notes-icon">📝</span>
                      <h4>{f.coach_name ? `${f.coach_name}'s note` : "Coach's note"}</h4>
                      {f.focus_tag && <span className="event-badge" style={{ marginLeft: 8 }}>{f.focus_tag}</span>}
                    </div>
                    <div className="notes-content">{f.note}</div>
                    {f.image_url && (
                      <a href={f.image_url} target="_blank" rel="noopener noreferrer" title="Open full size">
                        <img
                          src={f.image_url}
                          alt="Coached snapshot"
                          style={{ marginTop: 10, width: '100%', maxWidth: 480, borderRadius: 8, border: '1px solid #e5e7eb', display: 'block' }}
                        />
                      </a>
                    )}
                    {f.audio_url && (
                      <div style={{ marginTop: 10 }}>
                        <span style={{ fontSize: '0.85rem', color: '#6b7280' }}>🎙️ Voice note</span>
                        <audio controls src={f.audio_url} style={{ width: '100%', maxWidth: 480, display: 'block', marginTop: 4 }} />
                      </div>
                    )}
                  </div>

                  {(f.quality_score != null || f.injury_risk_category) && (
                    <div className="video-details">
                      {f.quality_score != null && (
                        <div className="detail-item"><strong>Quality:</strong> {Math.round(f.quality_score)}/100</div>
                      )}
                      {f.injury_risk_category && (
                        <div className="detail-item"><strong>Injury risk:</strong> {f.injury_risk_category}</div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default AthleteFeedbackView;
